import { useEffect, useState } from 'react'
import { useForm, Controller, Control, UseFormRegister } from 'react-hook-form'
import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    FormControl,
    FormLabel,
    FormHelperText,
    Input,
    Textarea,
    Button,
    Stack,
    HStack,
    Spinner,
    Box,
    Image,
    IconButton,
    Text,
    Flex,
    Icon
} from '@chakra-ui/react'
import { FiUploadCloud, FiX, FiTrash2, FiPlus } from 'react-icons/fi'
import { useDropzone } from 'react-dropzone'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { AdminField, AGE_GROUPS, ResourceConfig } from './schema'
import { recordToForm, formToPayload, extractYouTubeId, SongRow } from './transform'
import { useReferenceOptions } from './hooks'
import { useAlertService } from '@/shared/lib/useAlertService'
import {
    resizeFile,
    uploadFileToCloudinary,
    uploadToCloudinary
} from '@/shared/utils/imageUploadUtility'

interface Props {
    config: ResourceConfig
    record: Record<string, unknown> | null
    isOpen: boolean
    onClose: () => void
    onSubmit: (payload: Record<string, unknown>) => Promise<unknown>
    submitting?: boolean
}

// Render react-select menus above the Chakra drawer.
const selectMenuProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    styles: {
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 1500 })
    }
}

export default function AdminResourceForm({
    config,
    record,
    isOpen,
    onClose,
    onSubmit,
    submitting
}: Props) {
    const alertService = useAlertService()
    const { control, register, handleSubmit, reset } = useForm({
        defaultValues: recordToForm(config, record ?? undefined)
    })

    useEffect(() => {
        if (isOpen) reset(recordToForm(config, record ?? undefined))
    }, [isOpen, record, config, reset])

    const submit = async (values: Record<string, unknown>) => {
        try {
            const payload = formToPayload(config, values)
            await onSubmit(payload)
            onClose()
        } catch (e) {
            alertService.error(e instanceof Error ? e.message : 'Failed to save')
        }
    }

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader>
                    {record ? `Edit ${config.singular}` : `New ${config.singular}`}
                </DrawerHeader>
                <DrawerBody>
                    <form id="admin-resource-form" onSubmit={handleSubmit(submit)}>
                        <Stack spacing={5}>
                            {config.fields.map((field) => (
                                <FieldRenderer
                                    key={field.name}
                                    field={field}
                                    control={control}
                                    register={register}
                                />
                            ))}
                        </Stack>
                    </form>
                </DrawerBody>
                <DrawerFooter gap={3}>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="brand"
                        type="submit"
                        form="admin-resource-form"
                        isLoading={submitting}
                    >
                        Save
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

type AnyControl = Control<Record<string, unknown>>
type AnyRegister = UseFormRegister<Record<string, unknown>>

interface FieldProps {
    field: AdminField
    control: AnyControl
    register: AnyRegister
}

function Labelled({ field, children }: { field: AdminField; children: React.ReactNode }) {
    return (
        <FormControl isRequired={field.required}>
            <FormLabel mb={1}>{field.label}</FormLabel>
            {children}
            {field.helper && (
                <FormHelperText fontSize="xs">{field.helper}</FormHelperText>
            )}
        </FormControl>
    )
}

function FieldRenderer({ field, control, register }: FieldProps) {
    switch (field.type) {
        case 'verse':
            return (
                <Labelled field={field}>
                    <Stack spacing={2}>
                        <Input
                            placeholder="Verse text"
                            {...register(`${field.name}__text`)}
                        />
                        <Input
                            placeholder="Reference (e.g. 1 Samuel 15:22)"
                            {...register(`${field.name}__reference`)}
                        />
                    </Stack>
                </Labelled>
            )
        case 'ageGroups':
            return (
                <Labelled field={field}>
                    <MultiSelect
                        control={control}
                        name={field.name}
                        options={AGE_GROUPS.map((g) => ({ value: g, label: g }))}
                        placeholder="Select age groups…"
                    />
                </Labelled>
            )
        case 'tags':
            return (
                <Labelled field={field}>
                    <TagsSelect control={control} name={field.name} />
                </Labelled>
            )
        case 'enum':
            return (
                <Labelled field={field}>
                    <SingleSelect
                        control={control}
                        name={field.name}
                        options={(field.options ?? []).map((o) => ({
                            value: o,
                            label: o.replace(/_/g, ' ')
                        }))}
                    />
                </Labelled>
            )
        case 'reference':
            return <ReferenceField field={field} control={control} />
        case 'image':
            return <AssetDropzone field={field} control={control} kind="image" />
        case 'file':
            return (
                <AssetDropzone
                    field={field}
                    control={control}
                    kind={field.accept ?? 'any'}
                />
            )
        case 'color':
            return <ColorField field={field} control={control} />
        case 'youtube':
            return <YouTubeField field={field} control={control} />
        case 'songs':
            return <SongsBuilder field={field} control={control} />
        case 'textarea':
            return (
                <Labelled field={field}>
                    <Textarea
                        rows={3}
                        placeholder={field.placeholder}
                        {...register(field.name)}
                    />
                </Labelled>
            )
        default:
            return (
                <Labelled field={field}>
                    <Input
                        type={field.type === 'number' ? 'number' : 'text'}
                        placeholder={field.placeholder}
                        {...register(field.name)}
                    />
                </Labelled>
            )
    }
}

interface Option {
    value: string
    label: string
}

function SingleSelect({
    control,
    name,
    options,
    placeholder = 'Select…'
}: {
    control: AnyControl
    name: string
    options: Option[]
    placeholder?: string
}) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field: f }) => (
                <Select
                    {...selectMenuProps}
                    isClearable
                    placeholder={placeholder}
                    options={options}
                    value={options.find((o) => o.value === f.value) ?? null}
                    onChange={(opt) => f.onChange((opt as Option | null)?.value ?? '')}
                />
            )}
        />
    )
}

function MultiSelect({
    control,
    name,
    options,
    placeholder
}: {
    control: AnyControl
    name: string
    options: Option[]
    placeholder?: string
}) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field: f }) => {
                const current = Array.isArray(f.value) ? (f.value as string[]) : []
                return (
                    <Select
                        {...selectMenuProps}
                        isMulti
                        placeholder={placeholder}
                        options={options}
                        value={options.filter((o) => current.includes(o.value))}
                        onChange={(opts) =>
                            f.onChange((opts as Option[]).map((o) => o.value))
                        }
                    />
                )
            }}
        />
    )
}

function TagsSelect({ control, name }: { control: AnyControl; name: string }) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field: f }) => {
                const current = Array.isArray(f.value) ? (f.value as string[]) : []
                return (
                    <CreatableSelect
                        {...selectMenuProps}
                        isMulti
                        placeholder="Type and press Enter…"
                        formatCreateLabel={(input) => `Add “${input}”`}
                        value={current.map((v) => ({ value: v, label: v }))}
                        onChange={(opts) =>
                            f.onChange((opts as Option[]).map((o) => o.value))
                        }
                    />
                )
            }}
        />
    )
}

function ReferenceField({ field, control }: { field: AdminField; control: AnyControl }) {
    const { options, loading } = useReferenceOptions(
        field.refResource,
        field.refLabel ?? ((i) => String(i._id))
    )
    return (
        <Labelled field={field}>
            {loading ? (
                <Spinner size="sm" />
            ) : (
                <SingleSelect control={control} name={field.name} options={options} />
            )}
        </Labelled>
    )
}

function YouTubeField({ field, control }: { field: AdminField; control: AnyControl }) {
    return (
        <Controller
            control={control}
            name={field.name}
            render={({ field: f }) => {
                const id = extractYouTubeId(String(f.value ?? ''))
                return (
                    <Labelled field={field}>
                        <Input
                            placeholder="Paste a YouTube link or video ID"
                            value={(f.value as string) ?? ''}
                            onChange={(e) => f.onChange(e.target.value)}
                        />
                        {id && id.length === 11 && (
                            <HStack mt={2} spacing={3} align="center">
                                <Image
                                    src={`https://img.youtube.com/vi/${id}/default.jpg`}
                                    alt="Video preview"
                                    borderRadius="md"
                                    w="80px"
                                />
                                <Text fontSize="xs" color="green.600">
                                    ✓ Video found ({id})
                                </Text>
                            </HStack>
                        )}
                    </Labelled>
                )
            }}
        />
    )
}

function ColorField({ field, control }: { field: AdminField; control: AnyControl }) {
    return (
        <Controller
            control={control}
            name={field.name}
            render={({ field: f }) => {
                const value = String(f.value || field.placeholder || '#28275E')
                return (
                    <Labelled field={field}>
                        <HStack>
                            <Input
                                type="color"
                                value={
                                    /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#28275E'
                                }
                                onChange={(e) => f.onChange(e.target.value)}
                                w="64px"
                                p={1}
                            />
                            <Text fontSize="sm" color="gray.600">
                                {value}
                            </Text>
                        </HStack>
                    </Labelled>
                )
            }}
        />
    )
}

function AssetDropzone({
    field,
    control,
    kind
}: {
    field: AdminField
    control: AnyControl
    kind: 'image' | 'audio' | 'any'
}) {
    const alertService = useAlertService()
    const [uploading, setUploading] = useState(false)

    return (
        <Controller
            control={control}
            name={field.name}
            render={({ field: f }) => {
                const value = (f.value as string) ?? ''

                const upload = async (file: File) => {
                    try {
                        setUploading(true)
                        const res =
                            kind === 'image'
                                ? await uploadToCloudinary(await resizeFile(file))
                                : await uploadFileToCloudinary(
                                      file,
                                      kind === 'audio' ? 'video' : 'auto'
                                  )
                        if (res.secure_url) f.onChange(res.secure_url)
                        else throw new Error('Upload failed')
                    } catch (err) {
                        alertService.error(
                            err instanceof Error ? err.message : 'Upload failed'
                        )
                    } finally {
                        setUploading(false)
                    }
                }

                return (
                    <Labelled field={field}>
                        {value ? (
                            <Box position="relative" w="fit-content">
                                {kind === 'audio' ? (
                                    <Box
                                        borderWidth="1px"
                                        borderRadius="md"
                                        p={3}
                                        bg="gray.50"
                                        minW="260px"
                                    >
                                        <audio
                                            controls
                                            src={value}
                                            style={{ width: 260 }}
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        borderWidth="1px"
                                        borderRadius="md"
                                        overflow="hidden"
                                    >
                                        <Image
                                            src={value}
                                            alt="Preview"
                                            maxH="140px"
                                            objectFit="cover"
                                        />
                                    </Box>
                                )}
                                <IconButton
                                    aria-label="Remove file"
                                    icon={<FiX />}
                                    size="xs"
                                    colorScheme="red"
                                    position="absolute"
                                    top={1}
                                    right={1}
                                    onClick={() => f.onChange('')}
                                />
                            </Box>
                        ) : (
                            <Dropzone uploading={uploading} onFile={upload} kind={kind} />
                        )}
                        <Input
                            mt={2}
                            size="sm"
                            placeholder="…or paste an existing file URL"
                            value={value}
                            onChange={(e) => f.onChange(e.target.value)}
                        />
                    </Labelled>
                )
            }}
        />
    )
}

function Dropzone({
    uploading,
    onFile,
    kind
}: {
    uploading: boolean
    onFile: (file: File) => void
    kind: 'image' | 'audio' | 'any'
}) {
    const accept: Record<string, string[]> =
        kind === 'image'
            ? { 'image/*': [] }
            : kind === 'audio'
              ? { 'audio/*': ['.mp3', '.m4a', '.wav', '.aac'] }
              : {
                    'image/*': [],
                    'audio/*': ['.mp3', '.m4a', '.wav', '.aac']
                }
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept,
        multiple: false,
        onDrop: (files) => files[0] && onFile(files[0])
    })
    const label =
        kind === 'audio'
            ? 'Drag & drop an audio file, or click to choose'
            : kind === 'image'
              ? 'Drag & drop an image, or click to choose'
              : 'Drag & drop an image or audio file, or click to choose'
    const hint =
        kind === 'audio'
            ? 'MP3, M4A, WAV or AAC'
            : kind === 'image'
              ? 'PNG or JPG, up to 4MB'
              : 'PNG, JPG, MP3, M4A, WAV or AAC'
    return (
        <Flex
            {...getRootProps()}
            direction="column"
            align="center"
            justify="center"
            borderWidth="2px"
            borderStyle="dashed"
            borderColor={isDragActive ? 'brand.300' : 'gray.300'}
            bg={isDragActive ? 'brand.50' : 'gray.50'}
            borderRadius="md"
            p={6}
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ borderColor: 'brand.300', bg: 'brand.50' }}
        >
            <input {...getInputProps()} />
            {uploading ? (
                <HStack>
                    <Spinner size="sm" />
                    <Text fontSize="sm">Uploading…</Text>
                </HStack>
            ) : (
                <>
                    <Icon as={FiUploadCloud} boxSize={6} color="gray.400" />
                    <Text fontSize="sm" mt={2} fontWeight="medium">
                        {label}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                        {hint}
                    </Text>
                </>
            )}
        </Flex>
    )
}

function SongsBuilder({ field, control }: { field: AdminField; control: AnyControl }) {
    return (
        <Controller
            control={control}
            name={field.name}
            render={({ field: f }) => {
                const songs: SongRow[] = Array.isArray(f.value)
                    ? (f.value as SongRow[])
                    : []
                const update = (next: SongRow[]) => f.onChange(next)
                return (
                    <Labelled field={field}>
                        <Stack spacing={2}>
                            {songs.map((song, i) => (
                                <HStack key={i} align="start">
                                    <Input
                                        size="sm"
                                        placeholder="Song title"
                                        value={song.title}
                                        onChange={(e) =>
                                            update(
                                                songs.map((s, idx) =>
                                                    idx === i
                                                        ? { ...s, title: e.target.value }
                                                        : s
                                                )
                                            )
                                        }
                                    />
                                    <Input
                                        size="sm"
                                        placeholder="Audio link"
                                        value={song.url}
                                        onChange={(e) =>
                                            update(
                                                songs.map((s, idx) =>
                                                    idx === i
                                                        ? { ...s, url: e.target.value }
                                                        : s
                                                )
                                            )
                                        }
                                    />
                                    <IconButton
                                        aria-label="Remove song"
                                        icon={<FiTrash2 />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() =>
                                            update(songs.filter((_, idx) => idx !== i))
                                        }
                                    />
                                </HStack>
                            ))}
                            <Button
                                leftIcon={<FiPlus />}
                                size="sm"
                                variant="outline"
                                alignSelf="flex-start"
                                onClick={() => update([...songs, { title: '', url: '' }])}
                            >
                                Add song
                            </Button>
                        </Stack>
                    </Labelled>
                )
            }}
        />
    )
}
